/**
 * Image Exif
 *
 * EXIF parsing helpers used to read image orientation metadata.
 */

type ExifReadResult = {
    o: number;
    exifRel?: number;
    nextRel?: number;
};

type ExifCallback = (src: string) => void;
type DrawWithOrientation = (img: HTMLImageElement, orientation: number) => string;

class ImageExif {
    public static getExifOrientation(buffer: ArrayBuffer): number {
        try {
            const view = new DataView(buffer);
            if (view.getUint16(0, false) !== 0xffd8) return 1;
            const length = view.byteLength;
            let offset = 2;

            const readOrientationAtIFD = (
                tiffOffset: number,
                ifdRelOffset: number,
                little: boolean,
            ): ExifReadResult => {
                const start = tiffOffset + ifdRelOffset;
                if (start + 2 > view.byteLength) return { o: -1 };
                const entries = view.getUint16(start, little);
                let exifRel = 0;
                for (let index = 0; index < entries; index++) {
                    const entry = start + 2 + index * 12;
                    if (entry + 12 > view.byteLength) break;
                    const tag = view.getUint16(entry, little);
                    if (tag === 0x0112) {
                        const value = view.getUint16(entry + 8, little);
                        return { o: value || 1 };
                    }
                    if (tag === 0x8769) {
                        exifRel = view.getUint32(entry + 8, little);
                    }
                }
                const nextOffsetPosition = start + 2 + entries * 12;
                const nextRel =
                    nextOffsetPosition + 4 <= view.byteLength ? view.getUint32(nextOffsetPosition, little) : 0;
                return { o: -1, exifRel, nextRel };
            };

            while (offset < length) {
                const marker = view.getUint16(offset, false);
                offset += 2;
                if (marker === 0xffe1) {
                    const app1Length = view.getUint16(offset, false);
                    offset += 2;
                    if (view.getUint32(offset, false) === 0x45786966 && view.getUint16(offset + 4, false) === 0x0000) {
                        const tiffOffset = offset + 6;
                        const isLittle = view.getUint16(tiffOffset, false) === 0x4949;
                        if (view.getUint16(tiffOffset + 2, isLittle) !== 0x002a) return 1;
                        const firstRel = view.getUint32(tiffOffset + 4, isLittle);
                        if (firstRel < 0x00000008) return 1;

                        const r0 = readOrientationAtIFD(tiffOffset, firstRel, isLittle);
                        if (r0.o > 0) return r0.o;
                        if (r0.exifRel) {
                            const rEx = readOrientationAtIFD(tiffOffset, r0.exifRel, isLittle);
                            if (rEx.o > 0) return rEx.o;
                        }
                        if (r0.nextRel) {
                            const r1 = readOrientationAtIFD(tiffOffset, r0.nextRel, isLittle);
                            if (r1.o > 0) return r1.o;
                        }
                    }
                    offset += app1Length - 2;
                } else if ((marker & 0xff00) !== 0xff00) {
                    break;
                } else {
                    offset += view.getUint16(offset, false);
                }
            }
        } catch (_error) {
            void _error;
        }
        return 1;
    }

    public static fixDataUrlWithExif(
        src: string,
        callback: ExifCallback,
        drawWithOrientation: DrawWithOrientation,
    ): void {
        try {
            if (!/^data:image\/jpeg;base64,/i.test(src)) {
                callback(src);
                return;
            }

            const base64 = src.split(",")[1];
            if (!base64) {
                callback(src);
                return;
            }

            const binary = atob(base64);
            const len = binary.length;
            const bytes = new Uint8Array(len);
            for (let index = 0; index < len; index++) {
                bytes[index] = binary.charCodeAt(index);
            }
            const orientation = this.getExifOrientation(bytes.buffer) || 1;
            if (orientation === 1) {
                callback(src);
                return;
            }

            const img = new Image();
            img.onload = () => {
                try {
                    const corrected = drawWithOrientation(img, orientation);
                    callback(corrected);
                } catch (error) {
                    console.error(error);
                    callback(src);
                }
            };
            img.onerror = () => callback(src);
            img.src = src;
        } catch (error) {
            console.error(error);
            callback(src);
        }
    }
}

export { ImageExif };
